import React, { useRef, useState } from 'react'
import Excel from 'exceljs'
import IPageProps from '../../interfaces/page'
import { IExEssayImportRow, IExEssayImportPayloadRow } from '../../interfaces/ExEssayImportRow'
import { checkExEssayDuplicate, importExEssay } from '../../services/ExEssayService'
import { LoadingSpinner } from '../../components/loadingSpinner'
import TopNavPage2 from '../topNav2'
import FooterPage from '../footer'

const TERM_MAX_LEN = (idx: number) => (idx < 4 ? 80 : 50)

const cellWrapStyle: React.CSSProperties = { whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word' }

// Mirrors the server-side check in importExEssay — this is only a UX shortcut; the server enforces it authoritatively.
const FORMULA_RISK_CHARS = ['=', '+', '-', '@', '\t', '\r']

// Some users type the literal two/four-character sequence \n, \r\n or \r instead of pressing
// Alt+Enter in Excel — treat that the same as a real line break rather than showing it as-is.
const normalizeLineBreaks = (text: string): string => text.replace(/\\r\\n|\\n|\\r/g, '\n')

const PPTestImportPage: React.FunctionComponent<IPageProps> = props => {

    const [importType, setImportType] = useState<'A' | 'B'>('A')
    const [rows, setRows] = useState<IExEssayImportRow[]>([])
    const [isProcessing, setIsProcessing] = useState(false)
    const [isImporting, setIsImporting] = useState(false)
    const [resultMessage, setResultMessage] = useState<string | null>(null)

    const fileInputRef = useRef<HTMLInputElement | null>(null)

    const handleChangeType = (t: 'A' | 'B') => {
        if (t === importType) return
        setImportType(t)
        setRows([])
        setResultMessage(null)
    }

    const handleFileSelect = () => {
        if (fileInputRef.current) fileInputRef.current.click()
    }

    const readFileAsync = (file: File): Promise<ArrayBuffer> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = (event) => {
                const result = event.target?.result
                if (result) resolve(result as ArrayBuffer)
                else reject(new Error('Failed to read file.'))
            }
            reader.onerror = (error) => reject(error)
            reader.readAsArrayBuffer(file)
        })
    }

    const parseExcel = async (file: File): Promise<IExEssayImportRow[]> => {
        const arrayBuffer = await readFileAsync(file)
        const wb = new Excel.Workbook()
        await wb.xlsx.load(arrayBuffer)
        const ws = wb.getWorksheet(1)
        if (!ws) return []

        const headerMap: Record<string, number> = {}
        ws.getRow(1).eachCell((cell, colNumber) => {
            headerMap[cell.text.trim()] = colNumber
        })

        const getCellText = (row: Excel.Row, header: string): string => {
            const col = headerMap[header]
            if (!col) return ''
            return normalizeLineBreaks(row.getCell(col).text).trim()
        }

        const topicHeader = importType === 'A' ? '題目' : '標題'
        const termHeaderPrefix = importType === 'A' ? '選項' : '關鍵字'

        const parsedRows: IExEssayImportRow[] = []
        ws.eachRow((row, rowNumber) => {
            if (rowNumber === 1) return

            const starText = getCellText(row, '難易度')
            const topicText = getCellText(row, topicHeader)
            const contentText = getCellText(row, '短文')
            if (starText === '' && topicText === '' && contentText === '') return // skip blank rows

            const terms: (string | null)[] = []
            for (let i = 1; i <= 8; i++) {
                const v = getCellText(row, `${termHeaderPrefix}${i}`)
                terms.push(v === '' ? null : v)
            }

            const abstractText = importType === 'A' ? getCellText(row, '摘要') : ''
            const questionText = importType === 'B' ? getCellText(row, '問題') : ''
            const answerText = getCellText(row, '答案')

            const errors: string[] = []
            const starNum = Number(starText)
            if (starText === '' || Number.isNaN(starNum)) errors.push('難易度必填且需為數字')
            if (topicText === '') errors.push(`${topicHeader}必填`)
            else if (topicText.length > 30) errors.push(`${topicHeader}長度不可超過30字`)
            if (contentText === '') errors.push('短文必填')
            else if (contentText.length > 512) errors.push('短文長度不可超過512字')
            if (importType === 'A') {
                if (abstractText === '') errors.push('摘要必填')
                else if (abstractText.length > 100) errors.push('摘要長度不可超過100字')
            } else {
                if (questionText === '') errors.push('問題必填')
                else if (questionText.length > 100) errors.push('問題長度不可超過100字')
            }
            terms.forEach((t, i) => {
                if (t && t.length > TERM_MAX_LEN(i)) errors.push(`${termHeaderPrefix}${i + 1}長度不可超過${TERM_MAX_LEN(i)}字`)
            })
            if (answerText === '') errors.push('答案必填')
            else if (answerText.length > 8) errors.push('答案長度不可超過8字')

            // 避免 Excel 公式注入：內容日後會被 pptest3/pptest4 的「下載全部作答結果」重新匯出成 Excel，
            // 若欄位以 =/+/-/@ 開頭，開啟該匯出檔時可能被當成公式執行
            const formulaRiskFields: [string, string | null][] = [
                [topicHeader, topicText],
                ['短文', contentText],
                [importType === 'A' ? '摘要' : '問題', importType === 'A' ? abstractText : questionText],
                ['答案', answerText],
                ...terms.map((t, i): [string, string | null] => [`${termHeaderPrefix}${i + 1}`, t])
            ]
            formulaRiskFields.forEach(([label, value]) => {
                if (value && FORMULA_RISK_CHARS.includes(value[0])) {
                    errors.push(`${label}不可以 ${FORMULA_RISK_CHARS.filter((c: string) => c.trim()).join('、')} 等字元開頭（避免 Excel 公式注入）`)
                }
            })

            parsedRows.push({
                rowIndex: rowNumber - 1, // 扣除標題列，讓第一筆資料從 1 開始編號
                star: starNum,
                topic: topicText,
                content: contentText,
                abstract: importType === 'A' ? (abstractText || null) : null,
                question: questionText, // 長話短說固定問句由後端補上，讀出滋味取此欄
                terms,
                answer: answerText,
                errors,
                isDuplicate: false,
                ambiguousDuplicate: false,
                duplicateExistingNo: null,
                duplicateExistingContent: null,
                duplicateMessage: null,
                updateConfirmed: false,
                inFileDuplicate: false,
                inFileDuplicateMessage: null
            })
        })
        return parsedRows
    }

    // Flags rows that share Star+Topic with another row in the same file — the check-duplicate API only
    // compares against the database, so collisions within this batch would otherwise slip through unflagged.
    // Rows with a blank topic are excluded from grouping (they already get a required-field error, and
    // grouping many blank topics together would produce a misleading "duplicate" message).
    const detectInFileDuplicates = (list: IExEssayImportRow[]): IExEssayImportRow[] => {
        const groups = new Map<string, number[]>()
        list.forEach(r => {
            if (r.topic.trim() === '') return
            const key = `${r.star}::${r.topic}`
            const arr = groups.get(key) ?? []
            arr.push(r.rowIndex)
            groups.set(key, arr)
        })
        return list.map(r => {
            if (r.topic.trim() === '') return r
            const rowIndexes = groups.get(`${r.star}::${r.topic}`) ?? []
            if (rowIndexes.length <= 1) return r
            const others = rowIndexes.filter(idx => idx !== r.rowIndex)
            return {
                ...r,
                inFileDuplicate: true,
                inFileDuplicateMessage: `難易度+標題與第 ${others.join('、')} 列重複，同一份檔案內不可有重複資料，請修改 Excel 後重新上傳。`
            }
        })
    }

    const toPayloadRows = (list: IExEssayImportRow[]): IExEssayImportPayloadRow[] =>
        list.map(r => ({
            rowIndex: r.rowIndex,
            star: r.star,
            topic: r.topic,
            content: r.content,
            abstract: r.abstract,
            question: r.question,
            terms: r.terms,
            answer: r.answer,
            updateNo: (r.isDuplicate && !r.ambiguousDuplicate && r.updateConfirmed) ? r.duplicateExistingNo : null
        }))

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files && e.target.files[0]
        if (!file) return

        setIsProcessing(true)
        setResultMessage(null)
        try {
            const parsedRows = await parseExcel(file)
            if (parsedRows.length === 0) {
                setRows([])
                setResultMessage('Excel 檔案中沒有可匯入的資料列，請確認檔案內容與欄位標頭是否正確。')
                return
            }

            const inFileCheckedRows = detectInFileDuplicates(parsedRows)

            const dupResult = await checkExEssayDuplicate(importType, toPayloadRows(inFileCheckedRows))
            const dupMap = new Map(dupResult.duplicates.map(d => [d.rowIndex, d]))
            const finalRows = inFileCheckedRows.map(r => {
                const dup = dupMap.get(r.rowIndex)
                if (!dup) return r
                return {
                    ...r,
                    isDuplicate: true,
                    ambiguousDuplicate: dup.ambiguous,
                    duplicateExistingNo: dup.existingNo,
                    duplicateExistingContent: dup.existingContent,
                    duplicateMessage: dup.message
                }
            })
            setRows(finalRows)
        } catch (err) {
            console.error('Excel 解析或預檢失敗：', err)
            setRows([])
            setResultMessage('解析或預檢過程發生錯誤，請確認 Excel 檔案格式後重新上傳；詳細錯誤訊息請查看瀏覽器主控台 (Console)。')
        } finally {
            setIsProcessing(false)
            if (fileInputRef.current) fileInputRef.current.value = ''
        }
    }

    const handleToggleUpdateConfirmed = (rowIndex: number) => {
        setRows(prev => prev.map(r => r.rowIndex === rowIndex ? { ...r, updateConfirmed: !r.updateConfirmed } : r))
    }

    const hasValidationErrors = rows.some(r => r.errors.length > 0)
    const hasInFileDuplicates = rows.some(r => r.inFileDuplicate)
    const hasUnresolvedDuplicates = rows.some(r => r.isDuplicate && (r.ambiguousDuplicate || !r.updateConfirmed))
    const hasErrors = hasValidationErrors || hasInFileDuplicates || hasUnresolvedDuplicates

    const handleImport = async () => {
        if (rows.length === 0 || hasErrors) return
        setIsImporting(true)
        setResultMessage(null)
        try {
            const result = await importExEssay(importType, toPayloadRows(rows))
            if (result.success) {
                const parts: string[] = []
                if (result.inserted) parts.push(`新增 ${result.inserted} 筆`)
                if (result.updated) parts.push(`更新 ${result.updated} 筆`)
                setResultMessage(`匯入成功，共${parts.join('、') || '0 筆'}短文。`)
                setRows([])
            } else {
                setResultMessage(result.message || '新增失敗，請檢查欄位長度或格式是否正確後重新上傳。')
            }
        } catch (err) {
            console.error('匯入失敗：', err)
            setResultMessage('匯入過程發生錯誤，請稍後再試；詳細錯誤訊息請查看瀏覽器主控台 (Console)。')
        } finally {
            setIsImporting(false)
        }
    }

    return (
        <>
            {(isProcessing || isImporting) && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 9999,
                    background: 'rgba(255,255,255,0.7)',
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center'
                }}>
                    <LoadingSpinner text={isImporting ? '匯入中，請稍候...' : '解析與預檢中，請稍候...'} />
                </div>
            )}
            <div style={{ backgroundColor: '#FCF9F3' }}>
                <div><TopNavPage2 /></div>
                <div className="section pt-3">
                    <div className="container px-3 px-md-4">
                        <h5 className="title mb-3">PP 題目匯入</h5>

                        <div className="card p-3 mb-3">
                            <div className="mb-3">
                                <label className="me-3 fw-bold">匯入類別：</label>
                                <div className="form-check form-check-inline">
                                    <input className="form-check-input" type="radio" name="importType" id="importTypeA"
                                        checked={importType === 'A'} onChange={() => handleChangeType('A')} />
                                    <label className="form-check-label" htmlFor="importTypeA">長話短說</label>
                                </div>
                                <div className="form-check form-check-inline">
                                    <input className="form-check-input" type="radio" name="importType" id="importTypeB"
                                        checked={importType === 'B'} onChange={() => handleChangeType('B')} />
                                    <label className="form-check-label" htmlFor="importTypeB">讀出滋味</label>
                                </div>
                            </div>

                            <div>
                                <button type="button" className="btn btn-outline-secondary btn-round" onClick={() => handleFileSelect()}>
                                    上傳 Excel 檔案
                                </button>
                                <a className="btn btn-outline-secondary btn-round ms-2" href={`/imageDoc/ppTestImport/pp匯入_${importType === 'A' ? '長話短說' : '讀出滋味'}_範本.xlsx`} download>
                                    下載範本檔案
                                </a>
                                <input
                                    type="file"
                                    accept=".xlsx,.xls"
                                    ref={fileInputRef}
                                    style={{ display: 'none' }}
                                    onChange={handleFileChange}
                                />
                            </div>

                            {resultMessage && (
                                <div className="alert alert-info mt-3 mb-0">{resultMessage}</div>
                            )}
                        </div>

                        {rows.length > 0 && (
                            <div className="card p-3 table-responsive">
                                <table className="text-sm table table-striped mb-0" style={{ tableLayout: 'fixed', width: '100%' }}>
                                    <colgroup>
                                        <col style={{ width: '5%' }} />
                                        <col style={{ width: '6%' }} />
                                        <col style={{ width: '12%' }} />
                                        <col style={{ width: '26%' }} />
                                        <col style={{ width: '14%' }} />
                                        <col style={{ width: '17%' }} />
                                        <col style={{ width: '8%' }} />
                                        <col style={{ width: '12%' }} />
                                    </colgroup>
                                    <thead>
                                        <tr className="font-weight-bolder text-second" style={{ background: '#d9dce6' }}>
                                            <th className="p-2">列</th>
                                            <th className="p-2">難易度</th>
                                            <th className="p-2">{importType === 'A' ? '題目' : '標題'}</th>
                                            <th className="p-2">短文</th>
                                            <th className="p-2">{importType === 'A' ? '摘要' : '問題'}</th>
                                            <th className="p-2">{importType === 'A' ? '選項' : '關鍵字'}</th>
                                            <th className="p-2">答案</th>
                                            <th className="p-2">狀態</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row, i) => (
                                            <tr key={i} className={
                                                row.errors.length > 0 || row.inFileDuplicate || row.ambiguousDuplicate
                                                    ? 'table-danger'
                                                    : (row.isDuplicate && !row.updateConfirmed ? 'table-warning' : '')
                                            }>
                                                <td style={cellWrapStyle}>{row.rowIndex}</td>
                                                <td style={cellWrapStyle}>{row.star}</td>
                                                <td style={cellWrapStyle}>{row.topic}</td>
                                                <td style={cellWrapStyle}>
                                                    {row.isDuplicate && !row.ambiguousDuplicate && row.duplicateExistingContent !== null && (
                                                        <div className="text-muted small mb-1 border-bottom pb-1">
                                                            既有（{row.duplicateExistingNo}）：{row.duplicateExistingContent}
                                                        </div>
                                                    )}
                                                    {row.content}
                                                </td>
                                                <td style={cellWrapStyle}>{importType === 'A' ? (row.abstract ?? '') : row.question}</td>
                                                <td style={cellWrapStyle}>{row.terms.filter(t => t).join('、')}</td>
                                                <td style={cellWrapStyle}>{row.answer}</td>
                                                <td style={cellWrapStyle}>
                                                    {row.errors.length > 0
                                                        ? null
                                                        : row.inFileDuplicate
                                                            ? '檔案內重複，請修改 Excel'
                                                            : row.ambiguousDuplicate
                                                            ? '多筆重複，需人工處理'
                                                            : row.isDuplicate
                                                                ? (
                                                                    <div className="form-check">
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="checkbox"
                                                                            id={`update-${row.rowIndex}`}
                                                                            checked={row.updateConfirmed}
                                                                            onChange={() => handleToggleUpdateConfirmed(row.rowIndex)}
                                                                        />
                                                                        <label className="form-check-label small" htmlFor={`update-${row.rowIndex}`}>
                                                                            更新既有 {row.duplicateExistingNo}
                                                                        </label>
                                                                    </div>
                                                                )
                                                                : '新增'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                <div className="text-center mt-3">
                                    <button type="button" className="btn btn-round btn-primary btn-lg"
                                        disabled={hasErrors || isImporting} onClick={() => handleImport()}>
                                        確認匯入
                                    </button>
                                </div>

                                {hasValidationErrors && (
                                    <div className="alert alert-danger mt-3 mb-0">
                                        <div className="mb-2">以下資料有誤，請修改 Excel 檔案後重新上傳：</div>
                                        <ul className="mb-0">
                                            {rows.filter(r => r.errors.length > 0).map(r => (
                                                <li key={r.rowIndex}>
                                                    第 {r.rowIndex} 列（{r.topic || '未填寫' + (importType === 'A' ? '題目' : '標題')}）：{r.errors.join('；')}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {hasInFileDuplicates && (
                                    <div className="alert alert-danger mt-3 mb-0">
                                        <div className="mb-2">以下資料在此檔案內難易度+標題重複，請修改 Excel 檔案後重新上傳：</div>
                                        <ul className="mb-0">
                                            {rows.filter(r => r.inFileDuplicate).map(r => (
                                                <li key={r.rowIndex}>
                                                    第 {r.rowIndex} 列（{r.topic}）：{r.inFileDuplicateMessage}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {rows.some(r => r.ambiguousDuplicate) && (
                                    <div className="alert alert-danger mt-3 mb-0">
                                        <div className="mb-2">以下資料無法自動判斷比對對象，請聯絡系統管理者處理，或修改標題後重新上傳：</div>
                                        <ul className="mb-0">
                                            {rows.filter(r => r.ambiguousDuplicate).map(r => (
                                                <li key={r.rowIndex}>
                                                    第 {r.rowIndex} 列（{r.topic}）：{r.duplicateMessage}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {rows.some(r => r.isDuplicate && !r.ambiguousDuplicate && !r.updateConfirmed) && (
                                    <div className="alert alert-warning mt-3 mb-0">
                                        <div className="mb-2">以下資料與系統中既有短文標題重複，請比對內容後勾選「更新既有」以覆蓋，或修改 Excel 後重新上傳：</div>
                                        <ul className="mb-0">
                                            {rows.filter(r => r.isDuplicate && !r.ambiguousDuplicate && !r.updateConfirmed).map(r => (
                                                <li key={r.rowIndex}>
                                                    第 {r.rowIndex} 列（{r.topic}）：{r.duplicateMessage}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <FooterPage />
            </div>
        </>
    )
}

export default PPTestImportPage
